import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { welcomeWeekService } from '@/services/welcomeWeekService';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog';
import { CalendarClock, Loader2, MapPin, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  buildSourceJsonUrl,
  SCHEDULESLIST_URL,
  formatEventTime,
  fromDateTimeLocal,
  toDateTimeLocal,
  cleanEventTitleAndLocation,
  formatEventOnSubmit,
  labelFromLocationHtml,
} from './wwUi';
import { calendarHtml } from '@/utils/safeHtml';

/**
 * Whole-document list editor for a CalOne *source* calendar (the Omega master
 * is built from these). Unlike the group/master omega editor — which talks to a
 * per-event API — these source files live in the legacy CalOne fadder pipeline,
 * so we read the world-readable JSON and write the whole document back through
 * `/api/scheduleslist` (both CORS-enabled). Saving triggers CalOne's rebuild of
 * the merged `omegafadderperioden` master that every group merges from.
 *
 * The source JSON stores events as a Toast-UI map keyed by id (dates as
 * `{ _date }`, description in `body`, location as a maps anchor), so we map to a
 * flat form for editing and back on save, preserving every other field.
 */

interface SourceDoc {
  jsonvariable?: string;
  events?: Record<string, TuiEvent> | TuiEvent[];
  meta?: { rev?: number; updated_at?: string };
  [k: string]: unknown;
}

interface TuiEvent {
  id?: string | number;
  title?: string;
  body?: string;
  start?: { _date?: string } | string;
  end?: { _date?: string } | string;
  location?: string;
  calendarId?: string | number;
  [k: string]: unknown;
}

interface DisplayEvent {
  id: string;
  title: string;
  start: string; // ISO
  end: string; // ISO
  location: string; // HTML (maps anchor)
  description: string;
  raw?: {
    caloneBaseTitle?: string;
    caloneLocationText?: string;
    caloneLocationSource?: string;
    caloneLocationHtml?: string;
    caloneTitleLocationHtml?: string;
    caloneTitleLocationManaged?: boolean;
  };
}

interface FormState {
  title: string;
  start: string; // datetime-local
  end: string; // datetime-local
  location: string; // plain text label
  description: string;
}

const EMPTY_FORM: FormState = { title: '', start: '', end: '', location: '', description: '' };

interface Props {
  /** Calendar season code, e.g. "26h". */
  season: string;
  /** Source filename stem, e.g. "mtelsys_1_mtelsysfadder6457". */
  stem: string;
  /** Human label, e.g. "Omega Fadderprogram". */
  label: string;
  /** Explicit org slug (optional). */
  orgSlug?: string;
}

function isoOf(v: { _date?: string } | string | undefined): string {
  if (!v) return '';
  if (typeof v === 'string') return v;
  return v._date || '';
}



function eventsMap(doc: SourceDoc): Record<string, TuiEvent> {
  const ev = doc.events;
  if (Array.isArray(ev)) {
    return Object.fromEntries(ev.map((e, i) => [String(e.id ?? i + 1), e]));
  }
  return ev || {};
}

function nextId(map: Record<string, TuiEvent>): string {
  const nums = Object.keys(map).map((k) => Number(k)).filter((n) => Number.isFinite(n));
  return String((nums.length ? Math.max(...nums) : 0) + 1);
}

function buildTuiEvent(
  id: string,
  fields: { title: string; startIso: string; endIso: string; locationHtml: string; description: string; raw: any },
): TuiEvent {
  return {
    id,
    title: fields.title,
    body: fields.description || '',
    isAllDay: false,
    start: { _date: fields.startIso },
    end: { _date: fields.endIso },
    color: '#ffffff',
    isVisible: true,
    bgColor: '#9e5fff',
    dragBgColor: '#9e5fff',
    borderColor: '#9e5fff',
    calendarId: '1',
    category: 'time',
    dueDateClass: '',
    customStyle: '',
    isPending: false,
    isFocused: false,
    isReadOnly: false,
    isPrivate: false,
    location: fields.locationHtml || '',
    attendees: [],
    recurrenceRule: '',
    state: '',
    goingDuration: 0,
    comingDuration: 0,
    raw: fields.raw || null,
    __fe_id: Number(id) || id,
  };
}

export default function SourceEventEditor({ season, stem, label, orgSlug: orgSlugProp }: Props) {
  const { slug: orgSlugParam } = useParams<{ slug: string }>();
  const orgSlug = orgSlugProp || orgSlugParam;
  const [events, setEvents] = useState<DisplayEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | 'new' | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const docRef = useRef<SourceDoc | null>(null);

  const jsonUrl = buildSourceJsonUrl(season, stem);

  const load = useCallback(async () => {
    if (!jsonUrl) return;
    setLoading(true);
    try {
      const res = await fetch(`${jsonUrl}?${Date.now()}`, { cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const doc: SourceDoc = await res.json();
      const map = eventsMap(doc);
      doc.events = map; // normalize to a map for in-place edits
      docRef.current = doc;
      const list: DisplayEvent[] = Object.values(map).map((ev) => ({
        id: String(ev.id ?? ''),
        title: ev.title || '',
        start: isoOf(ev.start),
        end: isoOf(ev.end),
        location: ev.location || '',
        description: ev.body || '',
        raw: ev.raw as DisplayEvent['raw'],
      }));
      list.sort((a, b) => a.start.localeCompare(b.start));
      setEvents(list);
    } catch (error) {
      console.error('[SourceEventEditor] load failed', error);
      toast.error('Could not load calendar events.');
    } finally {
      setLoading(false);
    }
  }, [jsonUrl]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if ((event.data?.type === 'omega-action-success' || event.data?.type === 'ww-action-success')) {
        console.log('[SourceEventEditor] received success message from iframe, syncing master + refreshing list');
        void welcomeWeekService.syncWwMaster(orgSlug, season);
        void load();
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [load, orgSlug, season]);

  const openAdd = () => {
    setEditingId('new');
    setForm(EMPTY_FORM);
    setOpen(true);
  };

  const openEdit = (event: DisplayEvent) => {
    setEditingId(event.id);
    const { title, location } = cleanEventTitleAndLocation(event);
    setForm({
      title,
      start: toDateTimeLocal(event.start),
      end: toDateTimeLocal(event.end),
      location,
      description: event.description,
    });
    setOpen(true);
  };

  // Write the current docRef back to CalOne (triggers the master rebuild).
  const persist = async (doc: SourceDoc) => {
    const res = await fetch(SCHEDULESLIST_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(doc),
    });
    const body = await res.json().catch(() => ({}));
    if (res.status === 409 || res.status === 412) {
      await load();
      throw new Error('Calendar changed elsewhere — reloaded the latest copy, please retry.');
    }
    if (!res.ok) throw new Error(body?.message || `HTTP ${res.status}`);
  };

  const submit = async () => {
    if (!form.title.trim()) {
      toast.error('Title is required.');
      return;
    }
    if (!form.start || !form.end) {
      toast.error('Start and end times are required.');
      return;
    }
    const base = docRef.current;
    if (!base) {
      toast.error('Calendar not loaded yet.');
      return;
    }
    setSaving(true);
    try {
      const doc: SourceDoc = JSON.parse(JSON.stringify(base));
      const map = (doc.events || {}) as Record<string, TuiEvent>;
      const { displayTitle, locationHtml, raw } = formatEventOnSubmit(form.title, form.location);
      const fields = {
        title: displayTitle,
        startIso: fromDateTimeLocal(form.start),
        endIso: fromDateTimeLocal(form.end),
        locationHtml,
        description: form.description.trim(),
        raw,
      };
      if (editingId === 'new') {
        const id = nextId(map);
        map[id] = buildTuiEvent(id, fields);
      } else {
        const existing = map[String(editingId)];
        if (!existing) throw new Error('That event no longer exists — reload and retry.');
        existing.title = fields.title;
        existing.body = fields.description;
        existing.start = { _date: fields.startIso };
        existing.end = { _date: fields.endIso };
        existing.location = fields.locationHtml;
        existing.raw = fields.raw || null;
      }
      await persist(doc);
      // Saving the source regenerated the CalOne master; mirror it into Supabase
      // so the group mobile-friendly lists reflect the edit.
      void welcomeWeekService.syncWwMaster(orgSlug, season);
      toast.success(editingId === 'new' ? 'Event added.' : 'Event updated.');
      setOpen(false);
      await load();
    } catch (error) {
      console.error('[SourceEventEditor] save failed', error);
      toast.error(`Could not save event: ${(error as Error).message}`);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (event: DisplayEvent) => {
    if (!window.confirm(`Delete "${labelFromLocationHtml(event.title) || event.title || 'this event'}"? This cannot be undone.`)) return;
    const base = docRef.current;
    if (!base) return;
    setSaving(true);
    try {
      const doc: SourceDoc = JSON.parse(JSON.stringify(base));
      const map = (doc.events || {}) as Record<string, TuiEvent>;
      delete map[event.id];
      await persist(doc);
      void welcomeWeekService.syncWwMaster(orgSlug, season);
      toast.success('Event deleted.');
      await load();
    } catch (error) {
      console.error('[SourceEventEditor] delete failed', error);
      toast.error(`Could not delete event: ${(error as Error).message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardContent className="p-4 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2 text-sm font-medium text-muted-foreground min-w-0">
            <Pencil className="h-4 w-4 shrink-0" />
            <span className="shrink-0">Edit events</span>
            <Badge variant="secondary" className="max-w-full break-words">{label}</Badge>
          </div>
          <Button size="sm" onClick={openAdd} disabled={saving || loading} className="w-full sm:w-auto">
            <Plus className="h-4 w-4 mr-1.5" />
            Add event
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : events.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">No events yet.</p>
        ) : (
          <div className="grid gap-2">
            {events.map((event) => (
              <div key={event.id} className="space-y-1.5 rounded-md border p-3">
                <div className="flex items-center justify-between gap-2">
                  <h3
                    className="break-words font-medium leading-snug [&_a]:underline [&_a]:text-primary"
                    dangerouslySetInnerHTML={calendarHtml(event.title || 'Untitled')}
                  />
                  <div className="-mr-1 flex shrink-0 gap-0.5">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => openEdit(event)}
                      disabled={saving}
                      aria-label="Edit event"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive"
                      onClick={() => remove(event)}
                      disabled={saving}
                      aria-label="Delete event"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
                  <CalendarClock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span className="break-words">
                    {event.start && event.end ? formatEventTime(event.start, event.end) : '—'}
                  </span>
                </p>
                {event.location && (
                  <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
                    <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span
                      className="min-w-0 break-words [&_a]:underline [&_a]:text-primary"
                      dangerouslySetInnerHTML={calendarHtml(event.location)}
                    />
                  </p>
                )}
              </div>
            ))}
          </div>
        )}

        <p className="text-xs text-muted-foreground">
          These events are the <strong>{label}</strong> source. Saving here regenerates the merged{' '}
          master calendar, and the change flows out
          to every group on its next refresh.
        </p>
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
            className="space-y-4"
          >
            <DialogHeader>
              <DialogTitle>{editingId === 'new' ? 'Add event' : 'Edit event'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="src-title">Title</Label>
                <Input
                  id="src-title"
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  placeholder="e.g. Bar til bar"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="src-start">Start</Label>
                  <Input
                    id="src-start"
                    type="datetime-local"
                    value={form.start}
                    onChange={(e) => setForm((f) => ({ ...f, start: e.target.value }))}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="src-end">End</Label>
                  <Input
                    id="src-end"
                    type="datetime-local"
                    value={form.end}
                    onChange={(e) => setForm((f) => ({ ...f, end: e.target.value }))}
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="src-location">Location</Label>
                <Input
                  id="src-location"
                  value={form.location}
                  onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                  placeholder="e.g. Den Gode Nabo"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="src-description">Description</Label>
                <Textarea
                  id="src-description"
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)} disabled={saving}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
                {editingId === 'new' ? 'Add' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
